import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useFormPopupContext } from '../../contexts/FormPopupContext';
import { products } from '../../utils/data';
import HeroSection from '../../components/HeroSection';

const TowerMonitor: React.FC = () => {
  const product = products.find(p => p.id === 'tower-monitor');
  const { openFormPopup } = useFormPopupContext();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  if (!product) {
    return <div>Product not found</div>;
  }

  return (
    <div className="min-h-screen">
      {/* CINEMATIC HERO SECTION */}
      <HeroSection
        desktopImage="/images/hero-section-5.png"
        mobileImage="/images/mobile-hero-5.png"
        title={
          <>
            <span className="text-gradient block">Parallax PX 750</span>
            <span className="block text-3xl md:text-4xl text-secondary mt-2 font-normal">
              Luxury Pro Series
            </span>
          </>
        }
        subtitle="Commanding presence meets uncompromising clarity. The PX 750 Luxury Pro Series delivers 1500 watts of total system power through a 6.5-inch full range driver and soft dome silk tweeter, wrapped in a stunning high gloss wood finish."
        tagline={`Investment: ${product.priceRange}`}
      >
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
        >
          <button
            onClick={openFormPopup}
            className="btn-primary text-base sm:text-lg whitespace-nowrap"
          >
            Experience The Sound
          </button>
          <button
            onClick={openFormPopup}
            className="btn-secondary text-base sm:text-lg whitespace-nowrap"
          >
            Schedule Studio Consultation
          </button>
        </motion.div>
      </HeroSection>

      {/* ACOUSTIC PHILOSOPHY SECTION */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                  Studio-Grade Precision
                </h2>
                <div className="space-y-6 text-lg text-secondary/90 leading-relaxed">
                  <p>
                    The PX 750 Luxury Pro Series embodies the philosophy that true audio fidelity
                    begins with uncompromising engineering. Each monitor houses a 6.5-inch full range driver paired with
                    a soft dome silk tweeter, delivering 1500 watts of total system
                    power with surgical precision.
                  </p>
                  <p>
                    Housed in our signature High Gloss Wood cabinet, spanning a frequency response of 45Hz to 20kHz with less than 0.1% THD, ensuring every note,
                    every whisper, and every crescendo is reproduced with absolute fidelity.
                  </p>
                  <p>
                    This isn't merely a speaker system—it's a studio-grade instrument
                    engineered for those who refuse to compromise on clarity.
                  </p>
                </div>
              </motion.div>

              <motion.div
                className="relative max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                {/* Clean image presentation */}
                <div className="relative group">
                  <img
                    src="/images/parallax-tower-monitor-pair.png"
                    alt="Parallax PX 750 System"
                    className="w-full h-auto rounded-lg"
                  />

                  {/* Subtle hover effect overlay */}
                  <div className="absolute inset-0 bg-gradient-radial from-highlight/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none"></div>
                </div>

                {/* Premium caption styling */}
                <motion.div
                  className="mt-8 text-center"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className="text-highlight/60 text-xs uppercase tracking-[0.3em] mb-2">
                    High Gloss Wood Finish
                  </div>
                  <div className="text-secondary/40 text-sm uppercase tracking-wider">
                    Studio-Grade Accuracy
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* SYSTEM CONFIGURATION SHOWCASE */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-6 text-gradient">
                Complete Monitor System
              </h2>
              <p className="text-xl text-secondary/80 max-w-3xl mx-auto">
                Every component engineered to deliver uncompromising studio-grade performance
              </p>
            </motion.div>

            {/* System components visualization */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              {/* Monitor Left */}
              <motion.div
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">L</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Monitor Left</h3>
                <p className="text-sm text-secondary/80">2-Way bi-amped</p>
              </motion.div>

              {/* Monitor Right */}
              <motion.div
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">R</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Monitor Right</h3>
                <p className="text-sm text-secondary/80">Matched pair</p>
              </motion.div>

              {/* Stand Left */}
              <motion.div
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">SL</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Stand Left</h3>
                <p className="text-sm text-secondary/80">Precision isolation</p>
              </motion.div>

              {/* Stand Right */}
              <motion.div
                className="glass-effect rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">SR</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Stand Right</h3>
                <p className="text-sm text-secondary/80">Vibration damped</p>
              </motion.div>
            </div>

            {/* Key acoustic features */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.div
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">1500W Total</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Total System Power</h3>
                <p className="text-secondary/80">
                  Dedicated amplification for each driver ensures pristine signal separation
                  and effortless dynamic headroom.
                </p>
              </motion.div>

              <motion.div
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">&lt;0.1% THD</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Ultra-Low Distortion</h3>
                <p className="text-secondary/80">
                  Vanishingly low total harmonic distortion reveals every nuance
                  with absolute transparency and accuracy.
                </p>
              </motion.div>

              <motion.div
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">High Gloss Wood</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Stream & Connect</h3>
                <p className="text-secondary/80">
                  Seamless wireless connectivity with iOS and Android streaming
                  compatibility. Simple plug-and-play setup for effortless integration.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                Experience Studio Precision
              </h2>
              <p className="text-xl text-secondary/80 mb-12 leading-relaxed">
                Visit our private listening room or schedule a studio consultation
                to experience the commanding presence of the PX 750 Luxury Pro Series.
                Discover how studio-grade precision can transform your space.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 justify-center max-w-2xl mx-auto">
                <button
                  onClick={openFormPopup}
                  className="btn-primary text-lg px-12 py-4 flex-1 sm:flex-none whitespace-nowrap"
                >
                  Schedule Listening Session
                </button>
                <button
                  onClick={openFormPopup}
                  className="btn-secondary text-lg px-12 py-4 flex-1 sm:flex-none whitespace-nowrap"
                >
                  Request Studio Consultation
                </button>
              </div>

              <div className="mt-12 text-center">
                <p className="text-secondary/60 text-sm mb-4">
                  Includes 2 Studio Monitors + 2 Stands with professional setup
                </p>
                <div className="inline-flex items-center gap-4 text-xs text-secondary/40 uppercase tracking-wider">
                  <span>Studio-Grade Precision</span>
                  <span>•</span>
                  <span>Professional Installation</span>
                  <span>•</span>
                  <span>Acoustic Optimization</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popup handled globally */}
    </div>
  );
};

export default TowerMonitor;
